import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { type CampaignId, CampaignIdSchema, type LocationId, LocationIdSchema } from '@brightlocal/shared';

import { PaginationSchema } from '../../schemas';

export const CreateCitationBuilderResponseSchema = z
  .object({ campaign_id: CampaignIdSchema })
  .transform((data) => camelcaseKeys(data));

export type CreateCitationBuilderResponse = z.infer<typeof CreateCitationBuilderResponseSchema>;

export interface CreateCampaignParams {
  /** ID of the location to create the campaign for. */
  locationId: LocationId;
  /** Identifier of the funnel that triggered the request (e.g. `'sl-signup'`). */
  source: string;
}

const FindCampaignsItemSchema = z
  .object({
    campaign_id: CampaignIdSchema,
    location_id: LocationIdSchema
  })
  .transform((data) => camelcaseKeys(data));

export const FindCampaignsResponseSchema = z
  .object({
    total_count: z.number(),
    items: z.array(FindCampaignsItemSchema)
  })
  .transform((data) => camelcaseKeys(data));

export type FindCampaignsResponse = z.infer<typeof FindCampaignsResponseSchema>;

export interface FindCampaignsParams {
  /** Optional filter — return only campaigns belonging to this location. */
  locationId?: LocationId;
}

/**
 * Status returned by the lookup endpoint. The BE emits `''` before the worker
 * has picked the campaign up, then flips `processing` → `complete`. The empty
 * value is a valid "not started yet" state, so callers treat anything other
 * than `complete` as still-in-progress and keep polling.
 */
export const LookupStatusSchema = z.enum(['', 'processing', 'complete']);
export type LookupStatus = z.infer<typeof LookupStatusSchema>;

// Shapes mirror the Manage API DTOs (Modules\Cb\...\GetCampaignLookup):
// NAP fields are non-nullable strings; the whole `nap` object is nullable
// (a citation with no scraped data returns `nap: null`, key still present).
const LookupCitationNapSchema = z
  .object({
    name: z.string(),
    address: z.string(),
    phone: z.string(),
    postcode: z.string()
  })
  .transform((data) => camelcaseKeys(data));

// Per-field match flags between the scraped NAP and the campaign's truth.
// `?MatchingResults = null` in the DTO — may be null or absent, hence nullish.
const LookupCitationMatchingResultsSchema = z.object({
  name: z.boolean(),
  address: z.boolean(),
  phone: z.boolean(),
  postcode: z.boolean()
});

const LookupCitationSchema = z
  .object({
    domain: z.string(),
    profile_url: z.string(),
    nap: LookupCitationNapSchema.nullable(),
    matching_results: LookupCitationMatchingResultsSchema.nullish()
  })
  .transform((data) => camelcaseKeys(data));

export const CampaignLookupResultSchema = z
  .object({
    lookup_status: LookupStatusSchema,
    lookup_completed_at: z.string().nullable().optional(),
    citations: z.array(LookupCitationSchema).optional()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type CampaignLookupResult = z.infer<typeof CampaignLookupResultSchema>;

export interface GetCampaignLookupResultParams {
  campaignId: CampaignId;
}

export interface SubscribeLookupNotificationParams {
  campaignId: CampaignId;
  /** Email to notify when `lookup_status` flips to `complete`. */
  email: string;
  /** Originating platform identifier the BE records against the subscription. */
  platform: string;
}

/**
 * A single health snapshot (CB-4363): the weighted 0–100 `score` plus the
 * correct / missing / inconsistent directory breakdown it was computed from.
 */
const HealthSnapshotSchema = z.object({
  score: z.number(),
  directories: z.object({
    correct: z.number(),
    missing: z.number(),
    inconsistent: z.number()
  })
});

/**
 * Citation health score for a campaign (CB-4363).
 * `current` is today's snapshot; `projected` is the score once the selected
 * citations are completed. Either is `null` when no usable lookup is available
 * (lookup not complete, or a no-lookup country); `projected` equals `current`
 * when nothing is selected.
 */
export const CampaignHealthScoreSchema = z
  .object({
    current: HealthSnapshotSchema.nullable(),
    projected: HealthSnapshotSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type CampaignHealthScore = z.infer<typeof CampaignHealthScoreSchema>;
export type HealthSnapshot = z.infer<typeof HealthSnapshotSchema>;

export interface GetCampaignHealthScoreParams {
  campaignId: CampaignId;
}

/** Endpoint returns 201 with no payload — schema accepts any value. */
export const SubscribeLookupNotificationResponseSchema = z.unknown();

const AvailableCitationSchema = z
  .object({
    domain: z.string(),
    type: z.string(),
    domain_authority: z.number(),
    /** Capability flags behind the four Build preferences — the endpoint sends all four. */
    phone_verification: z.boolean(),
    is_sab_supported: z.boolean(),
    quick_listing: z.boolean(),
    client_verification: z.boolean()
  })
  .transform((data) => camelcaseKeys(data));

export const AvailableCitationsResponseSchema = z
  .object({
    data: z.array(AvailableCitationSchema),
    pagination: PaginationSchema
  })
  .transform((response) => camelcaseKeys(response, { deep: true }));

export type AvailableCitation = z.infer<typeof AvailableCitationSchema>;
export type AvailableCitationsResponse = z.infer<typeof AvailableCitationsResponseSchema>;

const PriceBreakdownSchema = z.object({ price: z.number(), credits: z.number() });

/** One rung of the publishers discount ladder: `count` selected publishers unlocks `discount` percent. */
const DiscountTierSchema = z.object({ count: z.number(), discount: z.number() });

/** A flat cost component of the estimate: bundle, duplicate removal, or express. */
const EstimateComponentSchema = z.object({
  id: z.string(),
  price: z.number(),
  credits: z.number()
});

/**
 * The post-discount totals every publisher-priced entry carries alongside its
 * undiscounted `price` / `credits`. `discounted_credits` may be fractional on a
 * per-item entry — the discount applies per item and rounding happens once on
 * the total, so pre-rounding an item would drift from it.
 */
const discountedTotals = {
  discounted_price: z.number(),
  discounted_credits: z.number()
};

/** One publisher's per-row cost inside the publishers component, before and after the bundle discount. */
const PublisherItemSchema = EstimateComponentSchema.extend(discountedTotals);

/**
 * The publishers component (CB-4483). `price`/`credits` are the undiscounted
 * subtotal of the selected publishers; `discounted_price`/`discounted_credits`
 * the same after the bundle discount; `items` breaks the subtotal down to one
 * row per itemised publisher.
 *
 * The subtotal also covers publisher-priced components that sit alongside this
 * one rather than in `items` — Active Sync is listed separately (see
 * {@link PublisherPricedComponentSchema}) but its price is already included here.
 */
const PublishersComponentSchema = EstimateComponentSchema.extend({
  ...discountedTotals,
  items: z.array(PublisherItemSchema)
});

/**
 * A discounted component whose price is already part of the publishers subtotal
 * but which the API lists separately, flagged by `is_publisher`. Active Sync is
 * the only such component today; it carries discounted totals like the publishers
 * component but has no `items` breakdown of its own.
 */
const PublisherPricedComponentSchema = EstimateComponentSchema.extend({
  ...discountedTotals,
  is_publisher: z.literal(true)
});

/**
 * The estimate's cost components: the publishers component, separately-listed
 * publisher-priced components (Active Sync), and flat components. The order is
 * not significant — consumers look a component up by `id`. The union is ordered
 * most-specific-first so a component carrying `items` or `is_publisher` never
 * falls through to the flat shape and loses its discounted totals.
 */
const EstimateComponentsSchema = z.array(
  z.union([PublishersComponentSchema, PublisherPricedComponentSchema, EstimateComponentSchema])
);

export const CampaignEstimateSchema = z
  .object({
    components: EstimateComponentsSchema,
    total: PriceBreakdownSchema
  })
  .transform((response) => camelcaseKeys(response, { deep: true }));

export type CampaignEstimate = z.infer<typeof CampaignEstimateSchema>;
export type EstimateComponent = CampaignEstimate['components'][number];

/** One publisher in the campaign's country-scoped price list — standalone, undiscounted. */
const PublisherPriceSchema = z.object({
  /** Canonical publisher slug (e.g. `dataaxle`, `as_plus`). */
  id: z.string(),
  /** Standalone undiscounted price in USD. */
  price: z.number(),
  /** Standalone undiscounted price in citation credits. */
  credits: z.number(),
  /**
   * Whether the publisher can list a business without publishing its address.
   * Active Sync always reports true. Required — consumers filter on it per
   * publisher, so an absent flag is drift worth surfacing.
   */
  is_sab_supported: z.boolean()
});

/**
 * Reference data for the Aggregators step (CB-4483): the campaign's country-scoped
 * publisher price list plus the publisher-bundle discount ladder. The source of
 * truth for which publishers are available; pricing an actual selection stays on
 * `/estimate`. No query params, no `total`.
 */
export const PublisherPricesResponseSchema = z
  .object({
    discount_ladder: z.array(DiscountTierSchema),
    publishers: z.array(PublisherPriceSchema)
  })
  .transform((response) => camelcaseKeys(response, { deep: true }));

export type PublisherPrices = z.infer<typeof PublisherPricesResponseSchema>;

export interface GetPublisherPricesParams {
  campaignId: CampaignId;
}

export interface GetCampaignEstimateParams {
  campaignId: CampaignId;
  /** A `packageName` from the campaign's `/packages` catalog. Omit to read the stored package. */
  packageName?: string;
  /**
   * Override the campaign's stored remove-duplicates flag. When truthy,
   * `packageName` must also be passed — otherwise the BE returns 400.
   */
  removeDuplicates?: boolean;
  /** Override the stored express flag. Same `packageName` constraint as removeDuplicates. */
  isExpress?: boolean;
  /** When falsy, Active Sync is excluded from the cost regardless of stored state. */
  isActiveSync?: boolean;
  /** Aggregator publisher slugs (dataaxle, neustar, foursquare, ypnetwork, gpsnetwork, locafy). */
  publishers?: readonly string[];
}

export interface GetAvailableCitationsParams {
  campaignId: CampaignId;
  /** 1-indexed page. Defaults to 1 BE-side. */
  page?: number;
  /** Items per page. BE default 500, max 1000. */
  numPerPage?: number;
}

/** BE returns 200 with no documented body shape on a successful PATCH. */
export const PatchCampaignOrderResponseSchema = z.unknown();

/**
 * Partial-update body for `PATCH /citation-builder/campaigns/{id}/order` per
 * CB-4340. Follows JSON Merge Patch (RFC 7396): omit a field = no change;
 * arrays are replaced wholesale (`[]` clears them); `null` resets a field.
 * Empty body `{}` is a valid no-op.
 */
export interface PatchCampaignOrderParams {
  campaignId: CampaignId;
  /** A `packageName` from the campaign's `/packages` catalog. */
  packageName?: string;
  /** Customer-selected citation IDs (fix + build). `[]` clears the manual list. */
  citations?: readonly string[];
  removeDuplicates?: boolean;
  isExpress?: boolean;
  /** Aggregator publisher slugs (dataaxle, neustar, foursquare, ypnetwork, gpsnetwork, locafynetwork). */
  publishers?: readonly string[];
  /** Active Sync add-on. Write side only — the response reports it as `active_sync`. */
  isActiveSync?: boolean;
  notes?: string;
  hideAddress?: boolean;
}

/** Confirm-order returns 204 No Content — there is no response body to parse. */
export const ConfirmCampaignOrderResponseSchema = z.unknown();

export interface ConfirmCampaignOrderParams {
  campaignId: CampaignId;
  /** Apply the express upgrade when confirming. Defaults to false BE-side. */
  express?: boolean;
}

/** A saved citation on the order — the domain is the only stable identifier the FE keys selections by. */
const SavedCitationSchema = z.object({
  domain: z.string(),
  type: z.string(),
  status: z.string(),
  domain_authority: z.number().nullable(),
  profile_url: z.string().nullable(),
  notes: z.string().nullable()
});

/** A saved publisher on the order. `type` carries the slug (e.g. `dataaxle`, `gpsnetwork`, `as_plus`); `name` is the display label. */
const SavedPublisherSchema = z.object({
  name: z.string(),
  type: z.string(),
  expiration: z.string().nullable()
});

/**
 * Order lifecycle timestamps, `Y-m-d H:i:s`. Only `updated_at` is modelled — the
 * creation / submission / purchase / completion dates the endpoint also returns
 * have no consumer yet.
 *
 * The column is nullable with no backfill, but the endpoint serialises a missing
 * value as an empty string rather than null, so orders never written to — and every
 * order predating the column — arrive as `''`.
 *
 * The BE bumps it on an order save and on a campaign data / name / status edit, but
 * not on lookup completion, note edits, or counter recalculations. Lookup completion
 * is excluded deliberately: it keeps `updated_at > lookup_completed_at` meaning
 * "the customer chose this".
 */
const OrderDatesSchema = z.object({
  updated_at: z.string()
});

/**
 * One campaign in the order (`campaigns[]`). Reflects the autosaved order for a
 * SAVED (unpaid) campaign — the FE hydrates the wizard store from it on entry so
 * a fresh device / cleared storage doesn't lose the saved selections.
 */
const CampaignOrderSchema = z.object({
  package_id: z.string().nullable(),
  /** Whether this order has been charged. A charged order no longer accepts changes. */
  paid: z.boolean(),
  duplicate_removal: z.boolean(),
  express: z.boolean(),
  /**
   * The saved Active Sync SKU (`as_basic` / `as_plus`), or null when off. The endpoint
   * reports the add-on only here, never inside `publishers[]`. Nullish because a
   * rejected order stops the whole restore, which costs more than one missing add-on.
   */
  active_sync: z.string().nullish(),
  /**
   * Whether the order hides the business address (service-area business). The
   * endpoint sends `false` when the flag has never been set, so it is never null.
   * Optional rather than required because an absent one still reads correctly as
   * "address shown", and rejecting it would fail the whole wizard restore.
   */
  hide_address: z.boolean().optional(),
  citations: z.array(SavedCitationSchema),
  publishers: z.array(SavedPublisherSchema),
  dates: OrderDatesSchema
});

/**
 * Single-campaign detail (`GET /citation-builder/{id}`). Carries the per-country
 * aggregator catalog (`availablePublishers`) and the saved order in `campaigns[]`
 * (primary first) that the flow hydrates the store from on entry.
 */
export const GetCampaignSchema = z
  .object({
    campaign_id: CampaignIdSchema,
    location_id: LocationIdSchema,
    available_publishers: z.array(z.string()),
    available_citations: z.array(z.string()),
    campaigns: z.array(CampaignOrderSchema)
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type GetCampaign = z.infer<typeof GetCampaignSchema>;
export type CampaignOrder = GetCampaign['campaigns'][number];
export type SavedCitation = CampaignOrder['citations'][number];
export type SavedPublisher = CampaignOrder['publishers'][number];

export interface GetCampaignParams {
  campaignId: CampaignId;
}

const CampaignPackageSchema = z
  .object({
    // Not a closed set: alongside the fixed bundles the BE mints a dynamically sized package from
    // the campaign's citation count (`cb30`) and an "everything available" one (`all`). Still
    // required to be non-empty — an unnamed package would reach `/estimate` as `package_name=`.
    package_name: z.string().min(1),
    citations_count: z.number(),
    components: z.object({
      package: PriceBreakdownSchema,
      duplicates_removal: PriceBreakdownSchema,
      express: PriceBreakdownSchema
    })
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

/**
 * Packages available for a campaign (`GET /citation-builder/{id}/packages`).
 * The BE resolves the per-customer catalog (hidden-package allow-list,
 * experiment gating, and a dynamically sized package based on the campaign's
 * citation count). The list carries no "default" flag and is sorted ascending
 * by `citationsCount`; the FE picks the default (see `resolveDefaultPackage`).
 */
export const CampaignPackagesResponseSchema = z
  .object({
    data: z.array(CampaignPackageSchema),
    pagination: PaginationSchema
  })
  .transform((response) => camelcaseKeys(response, { deep: true }));

export type CampaignPackage = z.infer<typeof CampaignPackageSchema>;
export type CampaignPackagesResponse = z.infer<typeof CampaignPackagesResponseSchema>;

export interface GetCampaignPackagesParams {
  campaignId: CampaignId;
  /** 1-indexed page. Defaults to 1 BE-side. */
  page?: number;
  /** Items per page. BE default 25, max 500. */
  numPerPage?: number;
  /**
   * Previews the packages as if the address were hidden — SAB-only citations
   * shrink the addressable count, which can prune larger packages. Omitted,
   * the BE falls back to the flag stored on the campaign.
   */
  hideAddress?: boolean;
}

/**
 * The customer's remaining Citation Builder credit balance (CB-4389). Account-
 * scoped, not per-campaign — the Review step reads it to show "Your balance" and
 * gate the pay-with-credits option.
 */
export const CreditBalanceResponseSchema = z
  .object({
    credits: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type CreditBalanceResponse = z.infer<typeof CreditBalanceResponseSchema>;
